/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { Logger } from '@quickplay/foundation';
import { PlaybackDataKey } from '../../../datakey/PlaybackDataKey';
import { GodavariAnalyticsReporterConfiguration } from '../../configuration/GodavariAnalyticsReporterConfiguration';
import {
  GODAVARI_NUMERIC_DEFAULTS,
  pickGodavariMetadata,
  toGodavariSubscription,
} from '../../util/GodavariMetadataUtil';
import { normalizeGodavariPlatform } from './GodavariSdk';
import { formatGodavariNumbers } from '../../util/GodavariNumberUtil';
import {
  GodavariBatchPayloads,
  GodavariApplicationMetadata,
  GodavariApplicationMetadataInput,
  GodavariApplicationPayload,
  GodavariDeviceInformation,
  GodavariEventMetadata,
  GodavariEventTime,
  GodavariHeartbeatFields,
  GodavariMetadata,
  GodavariMetadataInput,
  GodavariPlaybackFields,
} from '../model/GodavariPayload';
import { getOrCreateGodavariDeviceId } from '../../util/GodavariDeviceIdUtil';
import { getGodavariUserAgent, parseGodavariUserAgent } from '../../util/GodavariUserAgentUtil';

export interface GodavariPayloadContext {
  configuration: GodavariAnalyticsReporterConfiguration;
  metadata: GodavariMetadata;
  applicationMetadata: GodavariApplicationMetadata;
  logger?: Logger;
}

export function stringValue(value: unknown, fallback: string): string {
  return typeof value === 'string' && value !== '' ? value : fallback;
}

export function recordValue(value: unknown, logger?: Logger): Record<string, unknown> {
  if (value === undefined) {
    return {};
  }
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return { ...value };
  }
  logger?.warn('Invalid Godavari object metadata; ignoring its fields.');
  return {};
}

export function getEventTimeMetadata(timestamp: number): GodavariEventTime {
  return {
    event_wall_clock: String(timestamp),
    event_time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  };
}

export function buildCommonPayload(
  context: GodavariPayloadContext,
  metadata: GodavariEventMetadata,
  timestamp: number,
): GodavariPlaybackFields {
  const { configuration, metadata: playbackMetadata, applicationMetadata, logger } = context;
  const payload = {
    event_ftl: 'false' as const,
    event_exception: '',
    video_streaming_mode: 'streaming' as const,
    video_content_prefetch: 'no' as const,
    video_18plus: 'no' as const,
    video_time_to_first_byte: '0',
    video_playing_rate: '1x',
    ...playbackMetadata,
    ...applicationMetadata,
    ...pickGodavariMetadata(metadata),
    ...getEventTimeMetadata(timestamp),
    custom_tags: {
      ...configuration.appSessionData?.custom_tags,
      ...configuration.customTags,
      ...recordValue(playbackMetadata.custom_tags, logger),
      ...recordValue(metadata.custom_tags, logger),
    },
  };
  const normalized = formatGodavariNumbers(payload, GODAVARI_NUMERIC_DEFAULTS, logger);
  const contentId = normalized.video_content_id;
  if (contentId === undefined) {
    throw new Error('Godavari playback payload requires a valid video_content_id.');
  }
  // The Sony transformer substitutes ID 1 for a falsy numeric ID, but accepts "0".
  return { ...normalized, video_content_id: contentId === 0 ? '0' : contentId };
}

const BATCH_HEADERS = {
  videoSessionPulse: { video_event_type: 'Pulse', event_event_type: 'Pulse' },
  videoSessionHeartbeat: {
    video_event_type: 'VideoHeartbeat',
    event_event_type: 'VideoHeartbeat',
  },
  adsHeartbeat: {
    video_event_type: 'AdHeartbeat',
    event_event_type: 'AdHeartbeat',
  },
} as const;

type GodavariBatchInput<K extends keyof GodavariBatchPayloads> = Pick<GodavariBatchPayloads[K], 'event_data_list'> &
  (K extends 'videoSessionPulse' ? { pulse_event_count_sequence: number } : { video_sequence?: number });

export function buildBatchPayload<K extends keyof GodavariBatchPayloads>(
  context: GodavariPayloadContext,
  kind: K,
  batch: GodavariBatchInput<K>,
  timestamp: number,
): GodavariBatchPayloads[K] {
  const videoSequence = 'video_sequence' in batch ? batch.video_sequence : undefined;
  const payload = {
    ...buildCommonPayload(context, { video_sequence: videoSequence }, timestamp),
    ...batch,
    ...BATCH_HEADERS[kind],
  };
  // The kind selects matching input and headers; TypeScript cannot correlate indexed generic spreads.
  return payload as GodavariBatchPayloads[K];
}

export function withGodavariErrorFields(metadata: GodavariEventMetadata): GodavariEventMetadata {
  return {
    ...metadata,
    event_error_code: String(metadata.event_error_code ?? ''),
    event_error_message: metadata.event_error_message ?? '',
    event_exception: String(metadata.event_exception ?? ''),
  };
}

export function buildHeartbeatFields(
  playbackMetadata: GodavariMetadata,
  metadata: GodavariEventMetadata,
  timestamp: number,
  logger?: Logger,
): GodavariHeartbeatFields {
  return formatGodavariNumbers(
    {
      ...getEventTimeMetadata(timestamp),
      event_current_playback_rate: playbackMetadata.event_current_playback_rate,
      event_start_position:
        metadata[PlaybackDataKey.CONTENT_PLAYHEAD_POSITION_MILLISECONDS] ?? playbackMetadata.video_position,
      current_bitrate: playbackMetadata.video_current_bitrate,
      event_estimated_bandwidth: playbackMetadata.estimated_bandwidth_in_kbbs,
      event_available_buffer_duration: playbackMetadata.buffer_available_in_milliseconds,
      ...metadata,
    },
    {
      event_current_playback_rate: 1,
      event_start_position: 0,
      event_end_position: 0,
      event_duration: 0,
      current_bitrate: 0,
      event_estimated_bandwidth: 0,
      event_available_buffer_duration: 0,
      event_from_bitrate: 0,
      event_to_bitrate: 0,
      event_increment_number: 0,
    },
    logger,
  );
}

const AD_TECHNOLOGY: Record<string, string> = { 'client-side': 'Client Side', 'server-side': 'Server Side' };

export function toGodavariAdMetadata(metadata: GodavariEventMetadata, logger?: Logger): GodavariMetadata {
  const vastProperties = metadata.vastProperties || {};
  const adBreakInfo = metadata.adBreakInfo || {};
  const adBreak = metadata.adBreak || {};
  const adPosition = stringValue(adBreakInfo.contentTimePosition, stringValue(adBreak.position, '')).toLowerCase();
  let position: GodavariMetadataInput['ads_position'] = 'pre-roll';
  if (adPosition.includes('mid')) {
    position = 'mid-roll';
  } else if (adPosition.includes('post')) {
    position = 'post-roll';
  }
  const delivery = metadata.delivery || {};
  const wrapperAdIds = vastProperties.wrapperAdIds || [];
  const wrapperAdSystems = vastProperties.wrapperAdSystems || [];
  const wrapperCreativeIds = vastProperties.wrapperCreativeIds || [];
  const sequence = metadata.sequence ?? 0;
  const breakIndex = adBreakInfo.adBreakIndex;
  const adSessionId = typeof metadata.adSessionId === 'string' ? metadata.adSessionId.trim() : '';
  return formatGodavariNumbers(
    {
      // The vendor keeps a non-empty ads_session_id as ads-sid instead of generating its own.
      ...(adSessionId ? { ads_session_id: adSessionId } : ''),
      ads_asset_name: stringValue(vastProperties.adTitle, stringValue(metadata.title, 'NA')),
      ads_creative_id: stringValue(vastProperties.creativeId, 'NA'),
      ads_system: stringValue(vastProperties.adSystem, 'NA'),
      ads_description: stringValue(vastProperties.description, 'NA'),
      ads_advertiser: stringValue(vastProperties.advertiser, 'NA'),
      ads_stream_url: stringValue(delivery.adTagUrl, stringValue(metadata.mediaUrl, 'NA')),
      ads_current_ad_count: metadata.sequence ?? 0,
      ads_configured_ad_count: adBreakInfo.totalAds ?? adBreak.totalAds ?? 0,
      ads_current_bitrate: vastProperties.bitrate ?? 0,
      ads_length_duration:
        typeof metadata.durationMs === 'number' ? metadata.durationMs / 1000 : (metadata.duration ?? 0),
      ads_position: position,
      ads_campaign_id: stringValue(metadata.adID ?? metadata.adId, 'NA'),
      ads_advertiser_id: stringValue(vastProperties.advertiserId, 'NA'),
      ads_campaign_name: stringValue(vastProperties.adTitle, 'NA'),
      ads_technology: AD_TECHNOLOGY[delivery.technology as string] ?? 'NA',
      ads_stitcher: stringValue(delivery.stitcher, 'NA'),
      ads_is_slate: String(metadata.isFiller === true),
      ads_media_file_api_framework: stringValue(vastProperties.apiFramework, 'NA'),
      ads_manager_name: stringValue(delivery.managerName, 'NA'),
      ads_manager_version: stringValue(delivery.managerVersion, 'NA'),
      // The first ad of the wrapper chain is the ad itself when the response has no wrappers.
      ads_first_ad_system: stringValue(wrapperAdSystems[0], stringValue(vastProperties.adSystem, 'NA')),
      ads_first_ad_id: stringValue(wrapperAdIds[0], stringValue(metadata.adID ?? metadata.adId, 'NA')),
      ads_first_creative_id: stringValue(wrapperCreativeIds[0], stringValue(vastProperties.creativeId, 'NA')),
      ads_pod_ad_position: sequence,
      ads_pod_max_ad_count: adBreakInfo.totalAds ?? adBreak.totalAds ?? 0,
      // Pod indexes are 0-based and -1 marks a post-roll, so only known pods get an ordinal.
      ads_pod_position: typeof breakIndex === 'number' && breakIndex >= 0 ? breakIndex + 1 : 0,
      ad_pod_play_position: sequence,
    },
    {
      ads_current_ad_count: 0,
      ads_configured_ad_count: 0,
      ads_current_bitrate: 0,
      ads_length_duration: 0,
      ads_pod_ad_position: 0,
      ads_pod_max_ad_count: 0,
      ads_pod_position: 0,
      ad_pod_play_position: 0,
    },
    logger,
  );
}

/** SonyLIV reports the application OS in lower case, with a space for macOS ("mac os"). */
function toApplicationOperatingSystem(osName: string): string | undefined {
  if (osName === 'NA') {
    return undefined;
  }
  return osName === 'macOS' ? 'mac os' : osName.toLowerCase();
}

export function buildApplicationMetadata(
  configuration: GodavariAnalyticsReporterConfiguration,
  logger?: Logger,
): GodavariApplicationMetadataInput {
  const appData = configuration.appSessionData || {};
  const countryCode = configuration.countryCode || appData.app_geo_location || '';
  const deviceId = getOrCreateGodavariDeviceId();
  return {
    app_user_id: configuration.user?.id ?? deviceId,
    app_device_id: deviceId,
    app_subscribe_type: appData.app_subscribe_type ?? toGodavariSubscription(configuration.user?.type, logger),
    app_partner_id: configuration.application.product || configuration.application.name,
    app_version: configuration.application.version,
    app_build_version: configuration.application.build,
    app_operating_system: toApplicationOperatingSystem(getDeviceInformation(configuration).device_operating_system ?? 'NA'),
    app_hardware: (configuration.device?.type || configuration.device?.category)?.toLowerCase(),
    app_profile_id: configuration.user?.profileId,
    ...(configuration.user?.ppid ? { app_ppid: configuration.user.ppid } : {}),
    ...appData,
    app_platform_type: normalizeGodavariPlatform(
      appData.app_platform_type ?? configuration.device?.platformName ?? configuration.device?.platformType,
    ),
    app_geo_location: String(countryCode).toLowerCase(),
  };
}

export function buildApplicationPayload(
  applicationMetadata: GodavariApplicationMetadata,
  metadata: GodavariEventMetadata,
  timestamp: number,
  logger?: Logger,
): GodavariApplicationPayload {
  return formatGodavariNumbers(
    {
      ...applicationMetadata,
      ...pickGodavariMetadata(metadata),
      ...getEventTimeMetadata(timestamp),
    },
    { app_user_age: 0 },
    logger,
  );
}

const isKnown = (value: string | undefined): value is string =>
  typeof value === 'string' && value !== '' && value !== 'NA' && value.toLowerCase() !== 'web';

/** Picks the first known value; SonyLIV reports the browser's own OS and browser details. */
const firstKnown = (...values: Array<string | undefined>): string => values.find(isKnown) ?? 'NA';

export function getDeviceInformation(configuration: GodavariAnalyticsReporterConfiguration): GodavariDeviceInformation {
  const device = configuration.device;
  const provided = configuration.deviceInformation || {};
  const ua = parseGodavariUserAgent(getGodavariUserAgent());
  return {
    model_name: firstKnown(provided.model_name, device?.model, ua?.deviceModel),
    device_name: firstKnown(provided.device_name, device?.customDeviceName, ua?.deviceModel),
    device_model: firstKnown(provided.device_model, device?.model, ua?.deviceModel),
    device_operating_system: firstKnown(provided.device_operating_system, device?.osName, ua?.osName),
    device_operating_system_version: firstKnown(
      provided.device_operating_system_version,
      device?.osVersion,
      ua?.osVersion,
    ),
    browser_name: firstKnown(provided.browser_name, ua?.browserName),
    browser_version: firstKnown(provided.browser_version, ua?.browserVersion),
    manufacturer_name: firstKnown(
      provided.manufacturer_name,
      device?.customDeviceManufacturer,
      device?.brand,
      ua?.manufacturer,
    ),
  };
}
